import { t } from '@app/i18n';
import { Alert, Card, ErrorBanner } from '@zarya/ui';
import { useCanOperar } from '@app/auth/useCan';
import { useUpdateTenant } from './TenantSettings.api';
import {
  forUpdateTenant,
  type TenantConfig,
  type TenantFormValues,
} from './TenantSettingsModel';
import { TenantForm } from './TenantForm';

export interface TenantCardProps {
  tenant: TenantConfig;
}

/**
 * Card **Tenant** de T21 (FR-114, FR-115, FR-123) — US14, T425.
 *
 * - Cliente e Rótulo são locais e aparecem na topbar depois de salvar (14.1).
 * - **Este card não fala mais com o executor** (S-b (d)): o trust domain era o único
 *   campo daqui que mandava `config.apply`, e ele é do SITE. Com ele saíram o aviso
 *   de divergência (R19) e o `trust_domain_nao_suportado` — o primeiro virou o
 *   terceiro bloco do «Testar conexão», o segundo chega no Salvar do site.
 * - **Este card não sonda a superfície `/debug`** (aim-gl6): a sonda servia só ao botão
 *   «Zerar dados de demonstração», que saiu em 28/08, e no lab cada visita deixava um 404
 *   de rota de debug. Os comandos de desenvolvimento ficam na página oculta `/comandos`.
 */
export function TenantCard({ tenant }: TenantCardProps) {
  const canSave = useCanOperar('updateTenant');
  const save = useUpdateTenant();

  function toSave(valores: TenantFormValues) {
    save.mutate(forUpdateTenant(tenant, valores));
  }

  return (
    <Card title={t('tenantSettings.tenant.title')} subtitle={t('tenantSettings.tenant.subtitle')}>
      {save.error ? (
        // FR-123: mensagem amigável + código entre parênteses; nada foi gravado.
        <ErrorBanner error={save.error} escrita title={t('tenantSettings.tenant.error')} hideLink />
      ) : null}

      {save.isSuccess && !save.isPending ? (
        <Alert tone="info" title={t('tenantSettings.tenant.saved')} />
      ) : null}

      <TenantForm
        key={`${tenant.version}:${tenant.updated_at ?? ''}`}
        tenant={tenant}
        editavel={canSave}
        pending={save.isPending}
        onSubmit={toSave}
      />
    </Card>
  );
}
